import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, AlertCircle, Save, RotateCcw, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { profitCalculatorService } from '../../services/profitCalculatorService';
import { parseCurrencyBR } from '../../utils/currencyFormatter';
import AdminHeader from '../admin/components/AdminHeader';
import RevenueSection from './components/RevenueSection';
import DirectCostsSection from './components/DirectCostsSection';
import FixedCostsSection from './components/FixedCostsSection';
import DurationSection from './components/DurationSection';
import TaxSection from './components/TaxSection';
import ResultsDisplay from './components/ResultsDisplay';

import Button from '../../components/ui/Button';

/**
 * Convert Brazilian formatted value to number
 * Handles: "R$ 1.234,56", "1.234,56", "1234.56", "1234,56"
 * Returns: number or NaN if invalid
 */
const toNumberBR = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  
  // Remove R$, spaces, and convert string
  const cleaned = String(value)?.replace(/R\$/g, '')?.replace(/\s/g, '')?.trim();
  
  if (cleaned === '') return 0;
  
  // Remove thousand separators (dots) and replace decimal comma with dot
  const normalized = cleaned?.replace(/\./g, '')?.replace(',', '.'); // Remove dots (thousand separator) and replace comma with dot (decimal separator)
  
  const num = Number(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const ProfitCalculator = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const { lastUpdate, subscribeToCalculations, subscribeToTaxSettings, unsubscribe } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [refreshHistory, setRefreshHistory] = useState(0);
  
  const [formData, setFormData] = useState({
    // Revenue
    totalObra: '',
    valorJaFaturado: '',
    
    // Direct costs
    custoEmpreiteiro: '',
    equipeMensalPrestadores: '',
    visitasObraTotal: '',
    outrosCustosDiretos: '',
    
    // Fixed costs
    fixosMensaisEscritorio: '',
    
    // Duration
    duracaoSemanas: '',
    duracaoMeses: '',
    
    // Taxes
    dasPercentualEfetivo: '6.0',
    dasBaseCalculo: 'total_obra',
    prolaboreMensal: '5000.00',
    inssPercentualSeg: '11.0',
    
    // Metadata
    nomeObra: '',
    observacoes: ''
  });
  
  const [calculationResult, setCalculationResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        loadTaxDefaults();
      }
    }
  }, [user, profile, authLoading, navigate]);

  // Subscribe to real-time updates
  useEffect(() => {
    const calculationsChannel = subscribeToCalculations();
    const taxChannel = subscribeToTaxSettings();
    
    return () => {
      unsubscribe(calculationsChannel);
      unsubscribe(taxChannel);
    };
  }, [subscribeToCalculations, subscribeToTaxSettings, unsubscribe]);

  // Reload when real-time updates detected
  useEffect(() => {
    if (lastUpdate?.calculations) {
      setRefreshHistory(prev => prev + 1);
    }
  }, [lastUpdate?.calculations]);

  useEffect(() => {
    if (lastUpdate?.taxSettings) {
      loadTaxDefaults();
    }
  }, [lastUpdate?.taxSettings]);

  const loadTaxDefaults = async () => {
    try {
      setLoading(true);
      const settings = await profitCalculatorService?.getTaxSettings();
      setFormData(prev => ({
        ...prev,
        dasPercentualEfetivo: settings?.defaultDasPercentual?.toString() || '6.0',
        prolaboreMensal: settings?.defaultProlaboreMensal?.toString() || '5000.00',
        inssPercentualSeg: settings?.defaultInssPercentual?.toString() || '11.0'
      }));
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate duracaoMeses when duracaoSemanas changes
    if (field === 'duracaoSemanas' && value) {
      const meses = Math.ceil(parseFloat(value) / 4.33);
      setFormData(prev => ({ ...prev, duracaoMeses: meses?.toString() }));
    }
    
    // Clear validation error for this field
    if (validationErrors?.[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData?.totalObra || parseFloat(formData?.totalObra) <= 0) {
      errors.totalObra = 'Valor total da obra é obrigatório e deve ser maior que zero';
    }
    
    if (!formData?.duracaoSemanas || parseInt(formData?.duracaoSemanas) < 1) {
      errors.duracaoSemanas = 'Duração em semanas é obrigatória e deve ser no mínimo 1';
    }
    
    if (!formData?.duracaoMeses || parseInt(formData?.duracaoMeses) < 1) {
      errors.duracaoMeses = 'Duração em meses é obrigatória e deve ser no mínimo 1';
    }
    
    // Validate valorJaFaturado <= totalObra
    if (formData?.valorJaFaturado && formData?.totalObra) {
      const jaFaturado = parseFloat(formData?.valorJaFaturado);
      const total = parseFloat(formData?.totalObra);
      if (jaFaturado > total) {
        errors.valorJaFaturado = 'Valor já faturado não pode ser maior que o total da obra';
      }
    }
    
    // Validate no negative values
    const numericFields = [
      'totalObra', 'valorJaFaturado', 'custoEmpreiteiro', 'equipeMensalPrestadores',
      'visitasObraTotal', 'outrosCustosDiretos', 'fixosMensaisEscritorio',
      'dasPercentualEfetivo', 'prolaboreMensal', 'inssPercentualSeg'
    ];
    
    numericFields?.forEach(field => {
      if (formData?.[field] && parseFloat(formData?.[field]) < 0) {
        errors[field] = 'Valor não pode ser negativo';
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleCalculate = async () => {
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      setError('Por favor, corrija os erros no formulário');
      return;
    }
    
    try {
      setCalculating(true);
      
      // Convert all values using parseCurrencyBR (now handles Brazilian format)
      const receita_total = parseCurrencyBR(formData?.totalObra);
      const valor_ja_faturado = parseCurrencyBR(formData?.valorJaFaturado);
      const custo_empreiteiro = parseCurrencyBR(formData?.custoEmpreiteiro);
      const equipe_mensal = parseCurrencyBR(formData?.equipeMensalPrestadores);
      const visitas_total = parseCurrencyBR(formData?.visitasObraTotal);
      const outros_diretos = parseCurrencyBR(formData?.outrosCustosDiretos);
      const fixos_mensais = parseCurrencyBR(formData?.fixosMensaisEscritorio);
      const das_percent = parseCurrencyBR(formData?.dasPercentualEfetivo);
      const prolabore = parseCurrencyBR(formData?.prolaboreMensal);
      const inss_percent = parseCurrencyBR(formData?.inssPercentualSeg);
      const duracao_meses = parseInt(formData?.duracaoMeses) || 0;
      const duracao_semanas = parseInt(formData?.duracaoSemanas) || 0;
      
      // Validate for NaN
      const values = {
        receita_total,
        valor_ja_faturado,
        custo_empreiteiro,
        equipe_mensal,
        visitas_total,
        outros_diretos,
        fixos_mensais,
        das_percent,
        prolabore,
        inss_percent
      };
      
      const invalidFields = Object.entries(values)?.filter(([key, val]) => Number.isNaN(val))?.map(([key]) => key);
      
      if (invalidFields?.length > 0) {
        setError(`Existe um campo inválido. Verifique os valores: ${invalidFields?.join(', ')}`);
        console.error('Campos inválidos detectados:', invalidFields, values);
        return;
      }
      
      if (duracao_meses < 1 || duracao_semanas < 1) {
        setError('Duração inválida. Verifique semanas e meses.');
        return;
      }
      
      // Calculate intermediate values
      const equipe_total = equipe_mensal * duracao_meses;
      const fixos_total = fixos_mensais * duracao_meses;
      
      // DAS base calculation
      const valor_a_faturar = receita_total - valor_ja_faturado;
      const das_base = formData?.dasBaseCalculo === 'valor_a_faturar' ? valor_a_faturar : receita_total;
      const das_total = das_base * (das_percent / 100);
      
      // DARF calculation (will be done by service with IRRF)
      const inss_mensal = prolabore * (inss_percent / 100);
      
      const params = {
        totalObra: receita_total,
        valorJaFaturado: valor_ja_faturado,
        custoEmpreiteiro: custo_empreiteiro,
        equipeMensalPrestadores: equipe_mensal,
        visitasObraTotal: visitas_total,
        outrosCustosDiretos: outros_diretos,
        fixosMensaisEscritorio: fixos_mensais,
        duracaoSemanas: duracao_semanas,
        duracaoMeses: duracao_meses,
        dasPercentualEfetivo: das_percent,
        dasBaseCalculo: formData?.dasBaseCalculo,
        prolaboreMensal: prolabore,
        inssPercentualSeg: inss_percent,
        nomeObra: formData?.nomeObra,
        observacoes: formData?.observacoes
      };
      
      const result = await profitCalculatorService?.performCalculation(params);
      
      // Debug log - show all converted values and totals
      console.log('=== DEBUG: Valores Convertidos e Cálculo Final ===');
      console.log({
        receita_total,
        custo_empreiteiro,
        equipe_mensal,
        equipe_total,
        fixos_mensais,
        fixos_total,
        visitas_total,
        outros_diretos,
        das_total,
        inss_mensal,
        duracao_meses,
        'RESULTADO_total_gastos': result?.totalGastos,
        'RESULTADO_lucro': result?.lucroLiquido,
        'RESULTADO_darf_total': result?.darfTotal
      });
      
      // Validate result doesn't have NaN
      if (Number.isNaN(result?.totalGastos) || Number.isNaN(result?.lucroLiquido)) {
        setError('Erro no cálculo: resultado inválido. Verifique o console para detalhes.');
        console.error('Resultado com NaN detectado:', result);
        return;
      }
      
      setCalculationResult(result);
      setSuccess('Cálculo realizado com sucesso!');
    } catch (err) {
      setError('Erro ao calcular: ' + err?.message);
      console.error('Erro ao calcular:', err);
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    if (!calculationResult) {
      setError('Realize o cálculo antes de salvar');
      return;
    }
    
    try {
      setSaving(true);
      await profitCalculatorService?.saveCalculation(calculationResult);
      setSuccess('Cálculo salvo com sucesso!');
    } catch (err) {
      setError('Erro ao salvar: ' + err?.message);
      console.error('Erro ao salvar:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setFormData({
      totalObra: '',
      valorJaFaturado: '',
      custoEmpreiteiro: '',
      equipeMensalPrestadores: '',
      visitasObraTotal: '',
      outrosCustosDiretos: '',
      fixosMensaisEscritorio: '',
      duracaoSemanas: '',
      duracaoMeses: '',
      dasPercentualEfetivo: '6.0',
      dasBaseCalculo: 'total_obra',
      prolaboreMensal: '5000.00',
      inssPercentualSeg: '11.0',
      nomeObra: '',
      observacoes: ''
    });
    setCalculationResult(null);
    setValidationErrors({});
    setError('');
    setSuccess('');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader 
        user={user} 
        profile={profile} 
        userName={user?.email || profile?.name || 'Admin'} 
        onSignOut={handleSignOut} 
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Calculator className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Calculadora de Lucro</h1>
              <p className="text-base text-muted-foreground mt-1">Calcule a rentabilidade dos seus projetos</p>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-base text-destructive">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-base text-green-600">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Input Forms */}
          <div className="lg:col-span-2 space-y-6">
            <RevenueSection
              formData={formData}
              errors={validationErrors}
              onChange={handleInputChange}
            />
            
            <DirectCostsSection
              formData={formData}
              errors={validationErrors}
              onChange={handleInputChange}
            />
            
            <FixedCostsSection
              formData={formData}
              errors={validationErrors}
              onChange={handleInputChange}
            />
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            <DurationSection
              formData={formData}
              errors={validationErrors}
              onChange={handleInputChange}
            />
            
            <TaxSection
              formData={formData}
              errors={validationErrors}
              onChange={handleInputChange}
            />

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleCalculate}
                loading={calculating}
                disabled={calculating}
                size="lg"
              >
                <Calculator className="h-5 w-5 mr-2" />
                Calcular
              </Button>
              
              {calculationResult && (
                <Button
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving}
                  variant="success"
                  size="lg"
                >
                  <Save className="h-5 w-5 mr-2" />
                  Salvar Cálculo
                </Button>
              )}
              
              <Button
                onClick={handleClear}
                variant="outline"
                size="lg"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Limpar
              </Button>
            </div>
          </div>
        </div>

        {calculationResult && (
          <ResultsDisplay result={calculationResult} />
        )}
      </main>
    </div>
  );
};

export default ProfitCalculator;